import { Building2, Network, Plus, Power, RefreshCw, UserRound } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { AccessDetailHead, AccessLinkList } from "./AccessLinkList";
import { apiClient, type AccessCompany, type AccessUser, type CompanyGroup, type CompanyGroupDetail } from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import { ListSearch } from "@/shared/components/ListSearch";
import { ROLE_LABEL } from "@/shared/utils/roles";
import { AdminPageHeader } from "@/features/admin/AdminPageHeader";
import { useRevealDetail } from "./use-reveal-detail";
import { filterBySearch } from "@/shared/utils/text-search";

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * `master`: só o Máster cria núcleo e põe ou tira pessoas dele (RN-05,
 * 2026-10-22). O Administrador vê os núcleos dele, as pessoas de cada um,
 * e cuida das empresas.
 */
export function CompanyGroupsAdmin({ token, master = true }: { token: string; master?: boolean }) {
  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [users, setUsers] = useState<AccessUser[]>([]);
  const [companies, setCompanies] = useState<AccessCompany[]>([]);
  const [selected, setSelected] = useState<CompanyGroupDetail | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Núcleo inativo fica fora da lista por padrão, como o usuário inativo
  // (2026-10-14) — a um clique de voltar, pra poder reativar.
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmingDisable, setConfirmingDisable] = useState(false);
  const detailCache = useRef(new Map<number, CompanyGroupDetail>());
  const selectionSequence = useRef(0);
  const detailCard = useRef<HTMLDivElement>(null);
  useRevealDetail(detailCard, selected?.id);

  const load = useCallback(async (selectedId?: number) => {
    setBusy(true);
    try {
      const [nextGroups, nextUsers, nextCompanies, nextSelected] = await Promise.all([
        apiClient.listCompanyGroups(token),
        apiClient.listAccessUsers(token),
        apiClient.listAccessCompanies(token),
        selectedId === undefined
          ? Promise.resolve(null)
          : apiClient.companyGroup(token, selectedId)
      ]);
      setGroups(nextGroups);
      // O Máster vê todas as empresas: núcleo não lhe dá nada, e o servidor
      // recusa o vínculo. Fica fora das sugestões.
      setUsers(nextUsers.filter(user => user.status === "active" && user.role !== "master"));
      setCompanies(nextCompanies);
      if (nextSelected) {
        detailCache.current.set(nextSelected.id, nextSelected);
        setSelected(nextSelected);
      }
      setError(null);
    } catch (cause) {
      setError(errorMessage(cause, "Não foi possível carregar os núcleos."));
    } finally { setBusy(false); }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  /** Roda uma escrita e recarrega; devolve se deu certo (a confirmação
   *  só fecha no sucesso — na recusa, o erro aparece com ela aberta). */
  const act = async (operation: () => Promise<unknown>): Promise<boolean> => {
    setBusy(true); setError(null);
    try { await operation(); await load(selected?.id); return true; }
    catch (cause) { setError(errorMessage(cause, "Não foi possível concluir a operação.")); setBusy(false); return false; }
  };

  const create = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await apiClient.createCompanyGroup(token, { name, description: description || null });
      setName(""); setDescription("");
      await load(created.id);
    } catch (cause) {
      setError(errorMessage(cause, "Não foi possível concluir a operação."));
      setBusy(false);
    }
  };

  const open = async (group: CompanyGroup) => {
    const sequence = ++selectionSequence.current;
    const cached = detailCache.current.get(group.id);
    setConfirmingDisable(false);
    if (cached) {
      setSelected(cached);
      setError(null);
      return;
    }
    setBusy(true);
    try {
      const detail = await apiClient.companyGroup(token, group.id);
      if (sequence !== selectionSequence.current) return;
      detailCache.current.set(detail.id, detail);
      setSelected(detail);
      setError(null);
    }
    catch (cause) {
      if (sequence === selectionSequence.current) {
        setError(errorMessage(cause, "Não foi possível abrir o núcleo."));
      }
    }
    finally { if (sequence === selectionSequence.current) setBusy(false); }
  };

  const refresh = () => {
    detailCache.current.clear();
    void load(selected?.id);
  };

  const inactiveCount = groups.filter(group => !group.is_active).length;
  const statusFiltered = showInactive ? groups : groups.filter(group => group.is_active);
  const visibleGroups = filterBySearch(statusFiltered, search, group => [group.name, group.description]);

  return (
    <section className="copiloto-admin access-admin ajustes-panel admin-panel">
      <AdminPageHeader
        title="Núcleos"
        description="Grupos de empresas com usuários responsáveis e acessos herdados."
        actions={<button className="secondary-button access-refresh-button" onClick={refresh} disabled={busy}><RefreshCw size={16} /> Atualizar</button>}
      />
      {error && <p className="form-error-alert" role="alert">{error}</p>}

      {master && <div className="card access-create-card">
        <div className="card-title">
          <div>
            <Plus size={18} aria-hidden="true" />
            <h3>Criar núcleo</h3>
            <p>Quem entra num núcleo enxerga todas as empresas dele.</p>
          </div>
        </div>
        <div className="access-form-grid access-group-form">
          <label className="field">
            <span>Nome</span>
            <input placeholder="Nome do núcleo" value={name} onChange={event => setName(event.target.value)} />
          </label>
          <label className="field">
            <span>Descrição (opcional)</span>
            <input
              placeholder="Descrição do núcleo"
              value={description}
              onChange={event => setDescription(event.target.value)}
            />
          </label>
          <button
            className="primary-button access-create-button"
            disabled={busy || !name.trim()}
            onClick={() => void create()}
          >
            <Plus size={16} /> Criar núcleo
          </button>
        </div>
      </div>}

      <div className="access-split">
        <div className="card access-list-card">
          <div className="access-list-toolbar">
            <ListSearch
              value={search}
              onChange={setSearch}
              placeholder="Buscar núcleo"
              shown={visibleGroups.length}
              total={statusFiltered.length}
            />
            <div className="access-list-toolbar-row">
              <span className="ai-admin-caption">
                {statusFiltered.length === groups.length
                  ? `${groups.length} ${groups.length === 1 ? "núcleo" : "núcleos"}`
                  : `${statusFiltered.length} de ${groups.length} núcleos`}
              </span>
              <label className="admin-switch">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={event => setShowInactive(event.target.checked)}
                />
                <span>Mostrar inativos{inactiveCount > 0 ? ` (${inactiveCount})` : ""}</span>
              </label>
            </div>
          </div>
          <table className="ai-admin-table">
            <thead>
              <tr>
                <th>Núcleo</th>
                <th>Situação</th>
              </tr>
            </thead>
            <tbody>
              {visibleGroups.map(group => (
                <tr
                  key={group.id}
                  tabIndex={0}
                  aria-selected={selected?.id === group.id}
                  className={`${group.is_active ? "" : "row-inactive"} ${selected?.id === group.id ? "is-selected" : ""}`}
                  onClick={() => void open(group)}
                  onKeyDown={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      void open(group);
                    }
                  }}
                >
                  <td>
                    <strong>{group.name}</strong>
                    <span className="ai-admin-caption">{group.description || "Sem descrição"}</span>
                  </td>
                  <td>
                    <span className={`badge-pill ${group.is_active ? "success" : "inactive"}`}>
                      {group.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!busy && groups.length === 0 && <p className="access-empty">Nenhum núcleo criado.</p>}
          {!busy && groups.length > 0 && statusFiltered.length === 0 && (
            <p className="access-empty">
              Todos os núcleos estão inativos. Marque “Mostrar inativos” para vê-los.
            </p>
          )}
        </div>

        <div className="card access-detail-card" ref={detailCard}>
          {!selected ? (
            <div className="access-empty">
              <Network size={30} />
              <p>Selecione um núcleo para administrar.</p>
            </div>
          ) : (
            <>
              <AccessDetailHead
                icon={<Network size={18} />}
                title={selected.name}
                subtitle={selected.description || "Sem descrição"}
                status={selected.is_active ? "Ativo" : "Inativo"}
                tone={selected.is_active ? "success" : "inactive"}
              />
              <div className="group-detail-stats">
                <span><UserRound size={14} aria-hidden="true" /> {plural(selected.users.length, "usuário", "usuários")}</span>
                <span><Building2 size={14} aria-hidden="true" /> {plural(selected.companies.length, "empresa", "empresas")}</span>
              </div>

              {!selected.is_active && (
                <div className="user-detail-callout is-muted">
                  <Power size={16} aria-hidden="true" />
                  <p>
                    Núcleo inativo: não dá acesso a ninguém. Usuários e empresas continuam vinculados e voltam a
                    valer com a reativação.
                  </p>
                  <button
                    className="secondary-button"
                    disabled={busy}
                    onClick={() => void act(() => apiClient.setCompanyGroupEnabled(token, selected.id, true))}
                  >
                    Reativar
                  </button>
                </div>
              )}

              <AccessLinkList
                title="Usuários"
                hint={master
                  ? "Enxergam todas as empresas deste núcleo."
                  : "Enxergam todas as empresas deste núcleo. Quem põe e tira pessoas do núcleo é o Máster."}
                readOnly={!master}
                searchPlaceholder="Digite nome ou e-mail da pessoa"
                detail={item => item.email}
                icon={<UserRound size={14} />}
                available={users.filter(user => !selected.users.some(current => current.id === user.id))}
                linked={selected.users}
                label={item => `${item.name} · ${ROLE_LABEL[item.role]}`}
                onGrant={id => act(() => apiClient.grantGroupUser(token, selected.id, id as number)).then(() => undefined)}
                onRevoke={id => act(() => apiClient.revokeGroupUser(token, selected.id, id as number)).then(() => undefined)}
              />
              <AccessLinkList
                title="Empresas"
                hint="Ficam visíveis para todos os usuários do núcleo."
                searchPlaceholder="Digite o nome da empresa"
                icon={<Building2 size={14} />}
                isActive={item => item.is_active}
                available={companies.filter(company => !selected.companies.some(current => current.id === company.id))}
                linked={selected.companies}
                label={item => item.name}
                onGrant={id => act(() => apiClient.grantGroupCompany(token, selected.id, id as string)).then(() => undefined)}
                onRevoke={id => act(() => apiClient.revokeGroupCompany(token, selected.id, id as string)).then(() => undefined)}
              />

              {selected.is_active && (
                <section className="user-detail-section" aria-labelledby="group-detail-manage">
                  <h4 id="group-detail-manage">Gerenciar núcleo</h4>
                  <div className="access-manage">
                    <div className="access-manage-row">
                      <div className="access-manage-text">
                        <strong>Inativar núcleo</strong>
                        <p>Quem acessa as empresas só por este núcleo perde o acesso. Os vínculos ficam guardados.</p>
                      </div>
                      <div className="access-manage-action">
                        <button
                          className="secondary-button access-danger-button"
                          disabled={busy}
                          onClick={() => setConfirmingDisable(true)}
                        >
                          <Power size={15} aria-hidden="true" />
                          Inativar
                        </button>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* Inativar o núcleo só marca `disabled_on`: a pertença deixa
                  de valer (`membership.py` filtra núcleo inativo), mas
                  usuários e empresas seguem ligados a ele. */}
              {confirmingDisable && (
                <ConfirmDialog
                  title={`Inativar ${selected.name}?`}
                  description={`${plural(selected.users.length, "usuário perde", "usuários perdem")} o acesso que vem por este núcleo (${plural(selected.companies.length, "empresa", "empresas")}). Quem tem a empresa por outro núcleo ou vínculo direto continua vendo. Os vínculos ficam guardados e voltam a valer com a reativação.`}
                  confirmLabel="Inativar núcleo"
                  danger
                  loading={busy}
                  onConfirm={() =>
                    void act(() => apiClient.setCompanyGroupEnabled(token, selected.id, false)).then(ok => {
                      if (ok) setConfirmingDisable(false);
                    })
                  }
                  onCancel={() => setConfirmingDisable(false)}
                />
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
